import { useContext } from "react";
import {
    createFailure,
    DropdownOption,
    ensure,
    getById,
    mapToDropdownOption,
    validationFailed,
    ValidationResult,
    validationSuccessful
} from "sprint-common-web";

import { AspectRatiosDataContext } from "../../../../../common/data/lookup-data-providers/AspectRatiosDataProvider";
import { FramerateProfilesDataContext } from "../../../../../common/data/lookup-data-providers/FramerateProfilesDataProvider";
import { ManzanitaPresetsDataContext } from "../../../../../common/data/lookup-data-providers/ManzanitaPresetsDataProvider";
import { UpdateEncodeSpecificationMpeg1Request } from "../../../../../common/api/orders-api/encode-specification/requests/UpdateEncodeSpecificationMpeg1Request";
import { useAdminEncodeSpecificationApi } from "../../../api/useAdminEncodeSpecificationApi";
import { EncodeSpecificationContext } from "../../../context/EncodeSpecificationContext";
import {
    AspectType,
    aspectTypesOptions,
    EncodeSpecificationFramerate,
    FrameratePresets
} from "../../../context/EncodeSpecificationItem";
import { createFrameratePresets } from "../common/createFrameratePresets";
import { useEncodeSpecificationValidator } from "../useEncodeSpecificationValidator";
import { EditEncodeSpecificationMpeg1FormInputs } from "./EditEncodeSpecificationMpeg1Form";
import { EditEncodeSpecificationMpeg4FormInputs } from "./EditEncodeSpecificationMpeg4Form";

export const useEditEncodeSpecificationForm = () => {
    const { isNameUnique } = useEncodeSpecificationValidator();
    const { updateEncodeSpecificationMpeg1, updateEncodeSpecificationMpeg4 } = useAdminEncodeSpecificationApi();
    const {
        updateEncodeSpecificationMpeg1: updateEncodeSpecificationMpeg1Item,
        updateEncodeSpecificationMpeg4: updateEncodeSpecificationMpeg4Item
    } = useContext(EncodeSpecificationContext);
    const { manzanitaPresets } = useContext(ManzanitaPresetsDataContext);
    const { framerateProfiles } = useContext(FramerateProfilesDataContext);
    const { aspectRatios } = useContext(AspectRatiosDataContext);

    const aspectRatioOptions: DropdownOption[] = aspectRatios.map((item) => mapToDropdownOption(item));
    const framerateProfileOptions: DropdownOption[] = framerateProfiles.map((item) => mapToDropdownOption(item));
    const manzanitaPresetOptions: DropdownOption[] = manzanitaPresets.map((item) => mapToDropdownOption(item));

    const editEncodeSpecMpeg1 = async (itemId: string, inputs: EditEncodeSpecificationMpeg1FormInputs) => {
        const name = inputs.name;
        const slug = inputs.slug;
        const aspectType = Number(inputs.aspectType);

        const targetAspectRatio = aspectRatios.find((ratio) => ratio.id === inputs.targetAspectRatioId);
        ensure(targetAspectRatio);

        const frameratePresets = createFrameratePresets({
            framerateId23_976: inputs["framerate23_976"],
            framerateId24: inputs["framerate24"],
            framerateId25: inputs["framerate25"],
            framerateId29_97: inputs["framerate29_97"],
            framerateId30: inputs["framerate30"],
            framerateId50: inputs["framerate50"],
            framerateId59_94: inputs["framerate59_94"],
            framerateId60: inputs["framerate60"],
            framerateIdOther: inputs["framerateOther"],
            manzanitaPresets
        });

        await updateEncodeSpecificationMpeg1(
            getUpdateEncodeSpecMpeg1Request(
                itemId,
                name,
                frameratePresets,
                aspectType,
                targetAspectRatio.id,
                slug ?? undefined
            )
        ).then(() => {
            updateEncodeSpecificationMpeg1Item({
                id: itemId,
                name,
                frameratePresets,
                aspectType,
                slug,
                targetAspectRatio
            });
        });
    };

    const editEncodeSpecMpeg4 = async (itemId: string, inputs: EditEncodeSpecificationMpeg4FormInputs) => {
        const name = inputs.name;
        const ffmpegCommand = inputs.ffmpegCommand;
        const framerateProfileId = inputs.framerateProfileId;

        const targetAspectRatio = aspectRatios.find((ratio) => ratio.id === inputs.targetAspectRatioId);
        ensure(targetAspectRatio);

        await updateEncodeSpecificationMpeg4({
            id: itemId,
            ffmpegCommand,
            name,
            framerateProfileId,
            targetAspectRatioId: targetAspectRatio.id
        }).then(() => {
            updateEncodeSpecificationMpeg4Item({
                id: itemId,
                name,
                ffmpegCommand,
                framerateProfile: getById(framerateProfiles, inputs.framerateProfileId),
                targetAspectRatio
            });
        });
    };

    const validateNameUnique = async (name: string | undefined, itemId: string): Promise<ValidationResult> => {
        if (name && !isNameUnique(name, itemId)) {
            const failure = createFailure(`${name} is not unique name`, "name");

            return validationFailed([failure]);
        }

        return validationSuccessful();
    };

    return {
        aspectRatioOptions,
        framerateProfileOptions,
        manzanitaPresetOptions,
        aspectTypesOptions,
        validateNameUnique,
        editEncodeSpecMpeg1,
        editEncodeSpecMpeg4
    };
};

const getUpdateEncodeSpecMpeg1Request = (
    id: string,
    name: string,
    frameratePresets: FrameratePresets,
    aspectType: AspectType,
    targetAspectRatioId: string,
    slug?: number
): UpdateEncodeSpecificationMpeg1Request => {
    const presets: Record<EncodeSpecificationFramerate, string> = {
        "23.976": frameratePresets["23.976"].id,
        "24": frameratePresets["24"].id,
        "25": frameratePresets["25"].id,
        "29.97": frameratePresets["29.97"].id,
        "30": frameratePresets["30"].id,
        "50": frameratePresets["50"].id,
        "59.94": frameratePresets["59.94"].id,
        "60": frameratePresets["60"].id,
        OTHER: frameratePresets.OTHER.id
    };

    return {
        id,
        name,
        frameratePresets: presets,
        slug,
        aspectType,
        targetAspectRatioId
    };
};
